import React from 'react';
import { Loader2 } from 'lucide-react';
import { AuthMode } from '../hooks/useAuthForm';

const GoogleLogo: React.FC = () => (
  <svg width="18" height="18" viewBox="12 10 20 20" fill="none" aria-hidden="true">
    <path d="M31.6 20.2273C31.6 19.5182 31.5364 18.8364 31.4182 18.1818H22V22.05H27.3818C27.15 23.3 26.4455 24.3591 25.3864 25.0682V27.5773H28.6182C30.5091 25.8364 31.6 23.2727 31.6 20.2273Z" fill="#4285F4"/>
    <path d="M22 30C24.7 30 26.9636 29.1045 28.6181 27.5773L25.3863 25.0682C24.4909 25.6682 23.3454 26.0227 22 26.0227C19.3954 26.0227 17.1909 24.2636 16.4045 21.9H13.0636V24.4909C14.7091 27.7591 18.0909 30 22 30Z" fill="#34A853"/>
    <path d="M16.4045 21.9C16.2045 21.3 16.0909 20.6591 16.0909 20C16.0909 19.3409 16.2045 18.7 16.4045 18.1V15.5091H13.0636C12.3864 16.8591 12 18.3864 12 20C12 21.6136 12.3864 23.1409 13.0636 24.4909L16.4045 21.9Z" fill="#FBBC04"/>
    <path d="M22 13.9773C23.4681 13.9773 24.7863 14.4818 25.8227 15.4727L28.6909 12.6045C26.9591 10.9909 24.6954 10 22 10C18.0909 10 14.7091 12.2409 13.0636 15.5091L16.4045 18.1C17.1909 15.7364 19.3954 13.9773 22 13.9773Z" fill="#E94235"/>
  </svg>
);

const AppleLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg width="16" height="18" viewBox="0 0 814 1000" fill="currentColor" className={className} aria-hidden="true">
    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57.4-155.5-127.2c-58.9-82-101.2-208-101.2-328 0-193.9 125.8-296.5 249.4-296.5 65.7 0 120.5 43.1 161.7 43.1 39.2 0 100.3-45.8 174.2-45.8 28.2 0 129.3 2.6 196.2 99.5zm-270-181.7c31.3-36.9 53.5-88.1 53.5-139.3 0-7.1-.6-14.3-1.9-20.1-51 1.9-111.5 33.9-148.1 76.3-26.3 29.5-53.5 80.6-53.5 132.6 0 7.8.6 15.6 1.3 18.2 2.6.6 6.4 1.3 10.2 1.3 45.8 0 104-30.9 138.5-68.9z"/>
  </svg>
);

interface OAuthButtonsProps {
  mode: AuthMode;
  authLoading: boolean;
  theme: 'light' | 'dark';
  googleButtonLabel: string;
  appleButtonLabel: string;
  onGoogleLogin: () => void;
  onAppleLogin: () => void;
}

export const OAuthButtons: React.FC<OAuthButtonsProps> = ({
  mode,
  authLoading,
  theme,
  googleButtonLabel,
  appleButtonLabel,
  onGoogleLogin,
  onAppleLogin,
}) => {
  const isDark = theme === 'dark';

  return (
    <div className="space-y-2 w-full">
      {/* Google OAuth */}
      <button
        onClick={onGoogleLogin}
        disabled={authLoading}
        type="button"
        aria-label={googleButtonLabel}
        title={googleButtonLabel}
        className={`oauth-google-btn h-[44px] w-full flex items-center justify-center gap-3 rounded-full text-sm font-medium border cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 ease-theme hover:shadow-md ${
          isDark
            ? 'bg-[#131314] border-[var(--auth-border-warm)] text-[#e3e3e3]'
            : 'bg-white border-[var(--auth-border-warm)] text-[#1f1f1f]'
        }`}
      >
        {authLoading ? (
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
        ) : (
          <>
            <GoogleLogo />
            <span>{googleButtonLabel}</span>
          </>
        )}
      </button>

      {/* Apple OAuth */}
      <button
        onClick={onAppleLogin}
        disabled={authLoading}
        type="button"
        aria-label={appleButtonLabel}
        title={appleButtonLabel}
        className={`h-[44px] w-full flex items-center justify-center gap-3 rounded-full text-sm font-medium border cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 ease-theme hover:shadow-md ${
          isDark
            ? 'bg-[#1a1a1a] border-[var(--auth-border-warm)] text-white'
            : 'bg-[#1a1a1a] border-[#1a1a1a] text-white'
        }`}
      >
        {authLoading ? (
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
        ) : (
          <>
            <AppleLogo />
            <span>{appleButtonLabel}</span>
          </>
        )}
      </button>
    </div>
  );
};
